import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import {
  Gift, Copy, Check, Share, Crown, Users, WhatsappLogo, TwitterLogo, EnvelopeSimple, Lightning,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await axios.get(`${API}/referrals/me`);
        if (alive) setData(resp.data);
      } catch (err) {
        logger.error('Referrals load err:', err);
        toast.error('Could not load referrals');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="referrals-loading">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shareLink = `${origin}/auth?ref=${data.referral_code}`;
  const shareMessage = `I'm training on GRIT.FIT — AI-built home workouts, no equipment. Sign up with my code ${data.referral_code} and we both earn free Pro: ${shareLink}`;

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — long-press to select');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Train with me on GRIT.FIT', text: shareMessage, url: shareLink });
      } catch (err) { logger.warn('share cancelled', err); }
    } else {
      copy(shareMessage, 'Share message copied');
    }
  };

  const { totals, next_reward, referrals } = data;
  const progressPct = Math.round((next_reward.progress / next_reward.per_batch) * 100);

  return (
    <div className="max-w-4xl mx-auto" data-testid="referrals-page">
      {/* Header */}
      <div className="mb-8">
        <div className="label-tactical text-[#FF9500] mb-1">Refer & Earn</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">INVITE FRIENDS</h1>
        <p className="text-[#A1A1AA] text-sm mt-2 max-w-xl">
          Share your code. When <b className="text-white">{next_reward.per_batch} friends</b> complete their first workout, you unlock <b className="text-[#FF9500]">{next_reward.reward_days} days of Pro — free</b>.
        </p>
      </div>

      {/* Referral code card */}
      <div className="tactical-card p-6 mb-6 relative overflow-hidden" data-testid="referral-code-card">
        <div className="absolute -right-10 -top-10 opacity-[0.04]">
          <Gift weight="fill" className="w-48 h-48 text-[#FF9500]" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Gift weight="fill" className="w-5 h-5 text-[#FF9500]" />
            <span className="label-tactical">Your code</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div
              className="text-3xl sm:text-4xl font-black font-['Barlow_Condensed'] tracking-wider text-white bg-[#1C1C1E] px-5 py-3 border-2 border-dashed border-[#FF9500]/40"
              data-testid="referral-code-value"
            >
              {data.referral_code}
            </div>
            <button
              onClick={() => copy(data.referral_code, 'Code copied')}
              className="btn-secondary px-4 py-3 text-sm flex items-center gap-2"
              data-testid="referral-copy-code"
            >
              {copied ? <Check weight="bold" className="w-4 h-4 text-[#34C759]" /> : <Copy weight="bold" className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy code'}
            </button>
          </div>

          <div className="text-xs text-[#71717A] mb-1 uppercase tracking-wider">Your invite link</div>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <code className="flex-1 min-w-0 text-xs text-[#A1A1AA] bg-[#1C1C1E] px-3 py-2.5 border border-white/5 truncate" data-testid="referral-link-value">
              {shareLink}
            </code>
            <button
              onClick={() => copy(shareLink, 'Link copied')}
              className="btn-secondary px-3 py-2.5 text-xs flex items-center gap-1.5"
              data-testid="referral-copy-link"
            >
              <Copy weight="bold" className="w-3.5 h-3.5" /> Copy link
            </button>
          </div>

          {/* Share buttons */}
          <div className="flex flex-wrap gap-2">
            <button onClick={nativeShare} className="btn-primary px-4 py-2.5 text-sm flex items-center gap-2" data-testid="referral-share-native">
              <Share weight="bold" className="w-4 h-4" /> Share
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
              target="_blank" rel="noopener noreferrer"
              className="btn-secondary px-4 py-2.5 text-sm flex items-center gap-2"
              data-testid="referral-share-whatsapp"
            >
              <WhatsappLogo weight="fill" className="w-4 h-4 text-[#25D366]" /> WhatsApp
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`}
              target="_blank" rel="noopener noreferrer"
              className="btn-secondary px-4 py-2.5 text-sm flex items-center gap-2"
              data-testid="referral-share-twitter"
            >
              <TwitterLogo weight="fill" className="w-4 h-4 text-[#1DA1F2]" /> Twitter
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent('Train with me on GRIT.FIT')}&body=${encodeURIComponent(shareMessage)}`}
              className="btn-secondary px-4 py-2.5 text-sm flex items-center gap-2"
              data-testid="referral-share-email"
            >
              <EnvelopeSimple weight="bold" className="w-4 h-4" /> Email
            </a>
          </div>
        </div>
      </div>

      {/* Stats + Progress */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Stat icon={Users} color="#007AFF" value={totals.signed_up} label="Signed up" testid="stat-signed-up" />
        <Stat icon={Lightning} color="#34C759" value={totals.qualified} label="Qualified · did 1st workout" testid="stat-qualified" />
        <Stat icon={Crown} color="#FF9500" value={totals.pro_days_earned} label="Pro days earned" testid="stat-pro-days" />
      </div>

      {/* Progress to next reward */}
      <div className="tactical-card p-5 mb-6" data-testid="referral-progress">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Crown weight="fill" className="w-5 h-5 text-[#FF9500]" />
            <h2 className="text-lg font-bold tracking-tight">Next reward</h2>
          </div>
          <span className="text-xs text-[#A1A1AA]">
            {next_reward.progress}/{next_reward.per_batch} qualified · {next_reward.needed} more for {next_reward.reward_days} days free Pro
          </span>
        </div>
        <div className="h-3 bg-[#1C1C1E] border border-white/5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FF9500] to-[#FF3B30] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
            data-testid="referral-progress-bar"
          />
        </div>
      </div>

      {/* How it works */}
      <div className="tactical-card p-5 mb-6" data-testid="referral-how-it-works">
        <h2 className="text-lg font-bold tracking-tight mb-4">How it works</h2>
        <ol className="space-y-3 text-sm">
          {[
            'Share your code or link with friends.',
            'They sign up and enter your code during registration.',
            'They complete their first workout — that qualifies the referral.',
            `Every ${next_reward.per_batch} qualified friends = ${next_reward.reward_days} free days of GRIT.FIT Pro, stacked onto your current subscription.`,
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] flex items-center justify-center text-xs font-black font-['Barlow_Condensed']">{i + 1}</span>
              <span className="text-[#A1A1AA] pt-1">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Referral list */}
      <div className="tactical-card p-5 mb-24 lg:mb-6" data-testid="referral-list">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Users weight="bold" className="w-5 h-5 text-[#007AFF]" />
            Your referrals
          </h2>
          <span className="text-xs text-[#71717A]">{referrals.length} total</span>
        </div>
        {referrals.length === 0 ? (
          <div className="text-center py-8" data-testid="referral-list-empty">
            <Gift weight="duotone" className="w-12 h-12 text-[#71717A] mx-auto mb-3 opacity-50" />
            <p className="text-sm text-[#71717A]">No referrals yet. Share your code to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {referrals.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`referral-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{r.name}</div>
                  <div className="text-xs text-[#71717A] truncate">{r.email_masked}</div>
                </div>
                <StatusPill status={r.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, value, label, testid }) {
  return (
    <div className="tactical-card p-4" data-testid={testid}>
      <Icon weight="bold" className="w-5 h-5 mb-2" style={{ color }} />
      <div className="text-3xl font-black font-['Barlow_Condensed']">{value}</div>
      <div className="label-tactical text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    rewarded: { txt: 'Rewarded', cls: 'text-[#FF9500] bg-[#FF9500]/10 border-[#FF9500]/30' },
    qualified: { txt: 'Qualified', cls: 'text-[#34C759] bg-[#34C759]/10 border-[#34C759]/30' },
    pending: { txt: 'Awaiting 1st workout', cls: 'text-[#71717A] bg-white/5 border-white/10' },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${m.cls}`} data-testid={`status-${status}`}>
      {m.txt}
    </span>
  );
}
